// janus-engine/ability/counterspell.ts — lane RX #2 (2026-09-28): the COUNTERSPELL INTERRUPT in the browser, the twin of
// the retired PHP counterspell_check (+ tier lockout and the tie's wild-magic surge).
// Owner ruling (2026-09-28, "ship to browser"): the page receives the armed watches on its own caster (ability-truth.ts fetches them
// before the use, authority from the caster row) and the contest runs here; the commit re-claims exactly the watches this use claimed,
// atomically (ability_truth_commit_inc.php), so two uses can never both resolve one watch.
// Called from templateSpellRoll at PHP's point: after the pre-cast gates, before any roll dispatch or resource spend.
import { type CounterspellWatchSet, type JanusCtx, type Row, ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, upper } from './context.js';
import { type JanusInput } from './janus.js';
import { selectSkillForContestingRoll } from './select-skill-for-contesting-roll.js';
import { skillRollArr, skillRollIgnoreDisPreflight } from './skill-roll.js';
import { applyStatusEffect, skipImmuneStatus } from './statuses/apply-status-effect.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import { readRandomTableDef, rollRandomTable } from '../rolls/random-table.js';
import { randomTableChainPreDraw, WILD_MAGIC_SURGE_TABLE_ID } from './random-table-payload.js';
export { WILD_MAGIC_SURGE_TABLE_ID } from './random-table-payload.js';
import { counterspellCastingTimeEligible, counterspellItemTier, counterspellLockStatusForSchool, creatureKnowsSpellId } from '../reactions/counterspell-rules.js';
import { opposingTeams } from '../reactions/php-row-rules.js';
import { abilityRangeAllows } from './range-check.js';
import { TriggerInputUnavailable } from '../reactions/trigger-dispatch.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import type { AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import { selectCastingSkill } from './summon-caster-cantrips.js';
import { ctxProviderReaders, ctxSpellManaDiscount, discountedSpellMana, spellUncounterable } from './caster-spell-layers.js';
import { mysteriousDis, mysteriousUncounterable } from './cast-modifier-ports.js';
import { manaReductionDelta } from '../resources/reduction-guard.js';
import { resourceReductionReaders, setMana } from './resource-reduction.js';

const BENDING = 'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53';
const SPELL_LOCKED = '9DF8FF4C-33E8-8095-4876-ED3DBCD50B79';
const UNCOUNTERABLE_VARIABLES = ['charged_spell', 'charged_ability', 'spell_ritual', 'finish_casting_ritual_spell'];

const uuidOf = (entry: unknown): string => upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id') ?? record(entry)?.item_id);
const unavailable = <T>(fn: () => T, reason: string): T => {
  try { return fn(); } catch (error) { if (error instanceof TriggerInputUnavailable) handBack(`${reason}:${error.message}`); throw error; }
};

/** counterspell_check()'s scope: the casts an armed watch can interrupt at all (no watch lookup). */
export function counterspellScope(ctx: JanusCtx, input: JanusInput, ability: Row): boolean {
  if (ctx.trigger) return false; // trigger-fired casts are outside the action economy → uncounterable
  const v1 = input.useAbilityVariable1 == null ? '' : String(input.useAbilityVariable1);
  if (UNCOUNTERABLE_VARIABLES.includes(v1)) return false;
  if (String(itemField(ability, 'item_type') ?? '') !== 'spell') return false; // owner ruling: spells only, never curses
  return unavailable(() => counterspellCastingTimeEligible(twinReaders(ctx), ability), 'counterspell_check');
}

/** The watches armed on this caster, or a hand-back when the page never fetched them. [] = none (the ordinary cast). */
export function counterspellWatches(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): CounterspellWatchSet {
  if (!counterspellScope(ctx, input, ability)) return { watches: [] };
  const set = ctx.read.counterspellWatches?.(ecId(caster)) ?? null;
  if (!set) handBack('counterspell_check:watches-not-held');
  // Wizard v9 spells_cannot_be_counterspelled (circle-scoped, caster-spell-layers.ts): a protected spell is never examined — no watch is
  // claimed, fizzled or contested, so every armed watch stays armed for a counterable cast. Every caller (the spell template, the
  // timed-cast / persistent-area hand-back gates) reads the watches through here.
  // The verdict marks a protected spell EXEMPT even when the page read no armed watch (Codex review 2026-10-09: a watch armed between this
  // read and the commit would otherwise fail the commit's armed-set match, and the legacy route is refused). With no watch read, an
  // unprovable protection stays an ordinary cast (nothing to examine); with watches, it hands back.
  const sanguineProtection = mysteriousUncounterable(ctx, caster);
  const spellProtection = spellUncounterable(ctxProviderReaders(ctx), caster, ability);
  const protectedCast = sanguineProtection || spellProtection;
  if (protectedCast) { ctx.counterspellExempt = true; return { watches: [] }; }
  if (set.watches.length && (sanguineProtection === undefined || spellProtection === undefined)) handBack('counterspell_check:uncounterable-provider-unproved');
  return set;
}

function twinReaders(ctx: JanusCtx) {
  return { entries: (row: Row | null, key: string) => ctx.read.entries(row, key), value: (row: Row | null, key: string) => ctx.read.value(row, key),
    itemField: (row: Row | null, key: string) => itemField(row, key), definition: (id: string) => ctx.read.definition(id) } as never;
}

/**
 * counterspell_check(): examine the armed watches oldest-first; each is CLAIMED before examining it; an invalid watcher fizzles
 * publicly and the next is examined; the FIRST valid watcher contests (Bending Weapon Skill vs Bending Weapon Skill, the watcher at
 * clamp(counter level − cast level, −3..3) + 2 when it knows the spell) and pays its reaction + mana whatever the outcome. Success
 * interrupts the spell and applies the tier lockout; a tie interrupts with no lock and rolls the wild-magic surge table for the
 * WATCHER; a failure lets the spell through. Returns true when the spell is interrupted ($counterspelled).
 */
export function counterspellCheck(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): boolean {
  if (!counterspellScope(ctx, input, ability)) {
    const castingTime = ctx.read.value(ability, 'spell_casting_time');
    if (!ctx.trigger && itemField(ability, 'item_type') === 'spell' && (castingTime === 'reaction' || castingTime === '1ap')) {
      for (const watch of ctx.read.counterspellWatches?.(ecId(caster))?.watches ?? []) {
        insertRoll(ctx, { roll_type_1: 'counterspell_armed', roll_item_id: watch.itemId,
          by: ctx.members.get(upper(watch.watcherId)) ?? null, to: caster,
          rolls_full: [['—', `No contest: ${castingTime === 'reaction' ? 'reaction spells' : '1 AP spells'} cannot trigger Counterspell.`],
            ['—', 'The watch remains armed for a spell taking at least 2 AP.']] });
      }
    }
    return false;
  }
  const set = counterspellWatches(ctx, input, ability, caster);
  const watches = [...set.watches].sort((a, b) => a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1
    : a.triggerId < b.triggerId ? -1 : a.triggerId > b.triggerId ? 1 : 0);
  if (!watches.length) return false;
  // The commit accepts at most 128 claims; refuse larger watch sets before any draw.
  if (watches.length > 128) handBack('counterspell_check:too-many-watches');
  const read = { ...ctx.read, resolveItem: (entry: unknown) => {
    const row = record(entry);
    return ctx.read.definition(upper(typeof entry === 'string' ? entry : row?.ability_id ?? itemField(row, 'item_id')));
  } } as unknown as AbilityPreconditionReaders;
  const provider = casterLayerProvider(read, caster, 'spells_cannot_be_counterspelled_below_level');
  if (provider === undefined) handBack('counterspell_check:minimum-level-provider-unproved');
  const minimumLevel = provider === null ? 0 : requiredInt(ctx.read.value(provider, 'spells_cannot_be_counterspelled_below_level'), 'counterspell_check:invalid-minimum-level');
  const claims = ctx.counterspellClaims ??= { claimed: [], resolved: null };
  const spellId = upper(input.abilityId);
  for (const watch of watches) {
    const counterItem = ctx.read.definition(upper(watch.itemId));
    if (!counterItem) handBack('counterspell_check:counter-item-not-held');
    const counterLevel = ctx.read.value(counterItem, 'spell_level') == null ? 3 : requiredInt(ctx.read.value(counterItem, 'spell_level'), 'counterspell_check:invalid-counter-level');
    if (counterLevel < minimumLevel) {
      const watcher = ctx.members.get(upper(watch.watcherId));
      if (!watcher) handBack('counterspell_check:watcher-not-held');
      insertRoll(ctx, { roll_type_1: 'counterspell_fizzled', roll_item_id: watch.itemId, by: watcher, to: caster });
      continue;
    }
    claims.claimed.push(upper(watch.triggerId));
    const effect = ctx.read.value(counterItem, 'counterspelling_effect');
    if (effect == null || effect === '' || effect === 0 || effect === '0' || effect === false) continue;
    const watcher = ctx.members.get(upper(watch.watcherId));
    if (!watcher) handBack('counterspell_check:watcher-not-held');
    const fizzle = (): void => insertRoll(ctx, { roll_type_1: 'counterspell_fizzled', roll_item_id: watch.itemId, by: watcher, to: caster });
    const dead = ecLean(watcher, 'encounter_creature_dead');
    if (dead != null && requiredInt(dead, 'counterspell_check:invalid-dead') === 1) { fizzle(); continue; }
    if (!opposingTeams(watcher, caster, (row, key) => ctx.read.value(row, key))) { fizzle(); continue; }
    if (!abilityRangeAllows(ctx, watcher, caster, counterItem, 'counterspell_check:range', false)) { fizzle(); continue; }
    const reaction = ecLean(watcher, 'reaction'), mana = ecLean(watcher, 'mana');
    const reactionOk = reaction != null && requiredInt(reaction, 'counterspell_check:invalid-reaction') === 1;
    const manaCurrent = mana == null ? 0 : requiredInt(mana, 'counterspell_check:invalid-mana');
    // Wizard v9 spell_mana_discount on the WATCHER: its counter spell costs its level less the in-scope discount (floor 0)
    const counterCost = -manaReductionDelta(watcher, -discountedSpellMana(counterLevel, ctxSpellManaDiscount(ctx, watcher, counterItem)), resourceReductionReaders(ctx));
    if (!reactionOk || manaCurrent < counterCost) { fizzle(); continue; }
    // FIRST VALID WATCHER
    claims.resolved = upper(watch.triggerId);
    let targetLevel = ctx.read.value(ability, 'spell_level') == null ? 0 : requiredInt(ctx.read.value(ability, 'spell_level'), 'counterspell_check:invalid-spell-level');
    const v1 = input.useAbilityVariable1;
    const leveled = Number(ctx.read.value(ability, 'spell_level') ?? ctx.read.value(ability, 'menta_level') ?? 0) > 0;
    if (v1 != null && v1 !== '' && Number.isFinite(Number(v1)) && leveled) targetLevel = Math.max(targetLevel, Math.trunc(Number(v1)));
    const levelMod = Math.max(-3, Math.min(3, counterLevel - targetLevel));
    const knows = unavailable(() => creatureKnowsSpellId(twinReaders(ctx), watcher, spellId), 'counterspell_check:knows-spell') ? 2 : 0;
    // Both participants use Bending; a protected watcher must hand back before the caster's die too.
    skillRollIgnoreDisPreflight(ctx, caster, BENDING);
    skillRollIgnoreDisPreflight(ctx, watcher, BENDING);
    // a TIE rolls the surge table: it must be held and rollable before the contest draws (lane A3 RT-4 — the browser rolls the table)
    randomTableChainPreDraw(ctx, WILD_MAGIC_SURGE_TABLE_ID);
    // summons_gain_caster_cantrips: a summon's injected cantrip contests with the CONTROLLER's Bending (the summon still rolls the d20)
    const casterSkill = selectCastingSkill(ctx, caster, input.abilityId, [BENDING]);
    const watcherSkill = selectSkillForContestingRoll(ctx, watcher, [BENDING]);
    // Neither legacy counterspell roll sets $is_target: both use its false default, including the watcher.
    const originalTotal = skillRollArr(ctx, input, caster, casterSkill, null, 0, { isTarget: false });
    watcherSkill.modifier += levelMod + knows;
    watcherSkill.adv -= mysteriousDis(ctx, caster);
    const counterTotal = skillRollArr(ctx, { ...input, abilityId: upper(watch.itemId) }, watcher, watcherSkill, null, 0, { rollItemId: null, isTarget: false });
    ec(watcher).reaction = 0;
    setMana(ctx, watcher, manaCurrent - discountedSpellMana(counterLevel, ctxSpellManaDiscount(ctx, watcher, counterItem)));
    if (counterTotal > originalTotal) {
      ctx.counterspelled = true;
      insertRoll(ctx, { roll_type_1: 'counterspell_success', roll_item_id: watch.itemId, by: watcher, to: caster });
      tierLockout(ctx, input, ability, caster, watcher, counterItem, upper(watch.itemId));
    } else if (counterTotal < originalTotal) {
      insertRoll(ctx, { roll_type_1: 'counterspell_failed', roll_item_id: watch.itemId, by: watcher, to: caster });
    } else {
      ctx.counterspelled = true;
      insertRoll(ctx, { roll_type_1: 'counterspell_tie', roll_item_id: watch.itemId, by: watcher, to: caster });
      // random_table_roll() on the surge table, logged by the WATCHER to no one — rolled in the browser over the compiled bucket (lane
      // A3 RT-4): the rows carry the decided range snapshot (roll_meta.random_table); the commit records it, never looking it up.
      const out = rollRandomTable(WILD_MAGIC_SURGE_TABLE_ID, id => readRandomTableDef(id, ctx.read.definition(upper(id))), die => ctx.roll.d(die));
      if (!out.ok) handBack('random_table_roll:surge-table-not-held'); // unreachable after randomTableChainPreDraw
      for (const step of out.result.steps) insertRoll(ctx, { roll_type_1: step.roll_type_1, roll_type_2: step.roll_type_2,
        roll_item_id: step.roll_item_id, by: watcher, to: null, roll_result: step.roll_result, rolls_full: JSON.parse(step.rolls_full) as unknown[],
        dice: step.dice, die: step.die, mod: step.roll_mod, adv_dis_stacks: step.adv_dis_stacks, random_table: step.random_table });
    }
    return !!ctx.counterspelled;
  }
  return false;
}

/** counterspell_apply_tier_lockout(): T1 the countered spell's circle lock(s), T2 its sphere lock(s), T3 Spell Locked — applied by the
 *  watcher to the caster, duration from the COUNTER item read on the caster, no status log (the tier line is logged instead). */
function tierLockout(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, watcher: Row, counterItem: Row, counterItemId: string): void {
  const tier = counterspellItemTier(twinReaders(ctx), counterItem);
  const locks: string[] = [];
  let rollType: string;
  if (tier === 3) { locks.push(SPELL_LOCKED); rollType = 'counterspell_spell_locked'; }
  else {
    rollType = tier === 2 ? 'counterspell_sphere_locked' : 'counterspell_circle_locked';
    for (const entry of ctx.read.entries(ability, tier === 2 ? 'spell_sphere' : 'spell_circle')) {
      const lock = counterspellLockStatusForSchool(uuidOf(entry));
      if (lock && !locks.includes(lock)) locks.push(lock);
    }
  }
  if (!locks.length) return; // interrupted without a lock (a spell with no school data)
  const ends = getStatusEffectDurationTimes(ctx, counterItem, caster);
  const lockInput = { ...input, abilityId: counterItemId };
  const applicable = locks.filter(lock => {
    const definition = ctx.read.definition(lock);
    return !definition || !skipImmuneStatus(ctx, lock, definition, watcher, caster, false);
  });
  for (const lock of applicable) applyStatusEffect(ctx, lockInput, counterItem, lock, 'status_effect_applied', watcher, caster, ends, true, false);
  if (applicable.length) insertRoll(ctx, { roll_type_1: rollType, roll_item_id: counterItemId, by: watcher, to: caster });
}
